import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  Button,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useState } from "react";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import API from "../api";
import {
  startToast,
  updateToastError,
  updateToastSuccess,
} from "../utils/notification-loading";

const UploadImg = ({ style }) => {
  const [open, setOpen] = useState(false);
  const [, setImgProfile] = useState();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);
  const [, setFormat] = useState();
  const [isDragging, setIsDragging] = useState(false);

  const processFileForPreview = (file) => {
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();

      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setCurrentFile(file);
      };

      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      processFileForPreview(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFileForPreview(file);
    }
  };

  const handleConfirmUpload = () => {
    if (!currentFile) return;

    startToast();

    const reader = new FileReader();

    reader.onloadend = () => {
      const result = reader.result;

      if (typeof result !== "string") {
        updateToastError("Error on image upload");
        return;
      }

      const base64String = result.split(",")[1];
      const fileFormat = currentFile.type.split("/")[1];

      API.setProfileImage({
        img64: base64String,
      })
        .then(() => {
          updateToastSuccess("Data changed successfully");

          setImgProfile(base64String);
          setFormat(fileFormat);

          sessionStorage.setItem("profileImage", base64String);
          sessionStorage.setItem("imageType", fileFormat);

          handleClose();
        })
        .catch((error) => {
          
          updateToastError("Error on data change");
        });
    };

    reader.readAsDataURL(currentFile);
  };

  const handleClose = () => {
    setOpen(false);
    setPreviewUrl(null);
    setCurrentFile(null);
  };

  const handleRemovePreview = () => {
    setPreviewUrl(null);
    setCurrentFile(null);
  };

  return (
    <>
      <IconButton sx={style} onClick={() => setOpen(true)}>
        <CameraAltIcon />
      </IconButton>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography textAlign={"center"} fontSize={20}>
            Upload new profile image
          </Typography>
        </DialogTitle>

        <DialogContent>
          {previewUrl ? (
            <Box
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 2,
              }}
            >
              <Box
                component="img"
                src={previewUrl}
                alt="Profile image preview"
                sx={{
                  maxWidth: "100%",
                  maxHeight: 200,
                  borderRadius: 1,
                  objectFit: "contain",
                }}
              />

              <IconButton
                onClick={handleRemovePreview}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: "background.paper",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              component="label"
              sx={{
                mt: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: 200,
                border: "2px dashed",
                borderColor: isDragging ? "primary.main" : "divider",
                borderRadius: 2,
                bgcolor: isDragging ? "action.hover" : "background.paper",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
            >
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileSelect}
              />

              <CloudUploadIcon
                sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
              />

              <Typography variant="h6" color="text.primary">
                Click or Drag & Drop
              </Typography>

              <Typography variant="body2" color="text.secondary">
                PNG, JPG or WebP (max. 2MB)
              </Typography>
            </Box>
          )}
        </DialogContent>

        {previewUrl && (
          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
            <Button
              onClick={handleConfirmUpload}
              variant="contained"
              color="primary"
            >
              Confirm Upload
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default UploadImg;